/**
 * DisclosureOS — Board Dashboard (CSO Homepage)
 * ══════════════════════════════════════════════
 * Route: /dashboard · The first thing a CSO sees.
 *
 * Sections:
 *   1. Active Cycle: 7-stage pipeline with progress indicator
 *   2. Framework Readiness: per-FW completion bars
 *   3. Cross-Module KPIs: datapoints, evidence, claims, quality, audit readiness
 *   4. Flywheel Health: 5-spoke status
 *   5. Critical Alerts: CSDDD impacts, quality flags, overdue obligations
 *   6. AI Intelligence Summary: agents online, guardrails active, recent checks
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useAIHealth } from "../hooks/ai_module_hooks";

const C={bg:"#050a14",surface:"#0c1220",card:"#111c30",cardHover:"#162040",border:"#1a2744",borderHi:"#2a3a5c",text:"#e2e8f0",muted:"#6b7a96",dim:"#384660",blue:"#3b82f6",amber:"#f59e0b",emerald:"#10b981",purple:"#8b5cf6",cyan:"#06b6d4",red:"#ef4444",pink:"#ec4899",teal:"#14b8a6",orange:"#f97316",lime:"#84cc16",white:"#fff",indigo:"#6366f1"};

const Pl=({color:cl,children,small,glow})=><span style={{display:"inline-flex",alignItems:"center",gap:3,padding:small?"1px 6px":"3px 9px",borderRadius:99,fontSize:small?8:10,fontWeight:600,background:cl+"12",color:cl,border:`1px solid ${cl}22`,boxShadow:glow?`0 0 6px ${cl}33`:"none"}}>{children}</span>;
const Bar=({v,mx,cl,h=6,label})=><div style={{width:"100%"}}>
  {label&&<div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}><span style={{fontSize:8,color:C.muted}}>{label}</span><span style={{fontSize:8,fontWeight:700,color:cl}}>{Math.round(v/mx*100)}%</span></div>}
  <div style={{width:"100%",height:h,borderRadius:99,background:C.border,overflow:"hidden"}}><div style={{width:`${Math.min(v/mx*100,100)}%`,height:"100%",borderRadius:99,background:`linear-gradient(90deg,${cl}88,${cl})`,transition:"width .6s ease"}}/></div>
</div>;

const STAGES=[
  {id:"data_collection",l:"Upload",icon:"📥",c:C.cyan,step:1},
  {id:"mapping",l:"Map",icon:"🗺️",c:C.blue,step:2},
  {id:"review",l:"Review",icon:"🔍",c:C.amber,step:3},
  {id:"claim_generation",l:"Claim",icon:"✏️",c:C.purple,step:4},
  {id:"consistency",l:"Verify",icon:"🔄",c:C.emerald,step:5},
  {id:"approval",l:"Approve",icon:"✅",c:C.red,step:6},
  {id:"export",l:"Export",icon:"📤",c:C.teal,step:7},
];

const API_BASE_LOCAL = typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL : "";
async function apiFetchLocal(path) {
  try {
    const {apiFetch} = await import("../services/api_service");
    return apiFetch(path);
  } catch {
    try { const r = await fetch(`${API_BASE_LOCAL}${path}`, {credentials:"include"}); if(!r.ok) return null; const _t = await r.text(); try{return JSON.parse(_t);}catch{return null;} } catch { return null; }
  }
}

export default function BoardDashboard() {
  const [cycle, setCycle] = useState(null);
  const [readiness, setReadiness] = useState({});
  const [loading, setLoading] = useState(true);
  const {agents: aiAgents, health: aiHealth, fetchHealth: refetchAI} = useAIHealth();

  // Fetch active cycle + readiness
  const fetchData = useCallback(async () => {
    setLoading(true);
    const [cycleData, readinessData] = await Promise.all([
      apiFetchLocal("/cycles/active"),
      apiFetchLocal("/graph/export/readiness"),
    ]);
    if (cycleData && !cycleData._error && !cycleData.error) setCycle(cycleData);
    if (readinessData?.frameworks) {
      const map = {};
      readinessData.frameworks.forEach(f => { map[f.framework] = f; });
      setReadiness(map);
    }
    refetchAI();
    setLoading(false);
  }, [refetchAI]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const currentStageIdx = cycle ? STAGES.findIndex(s => s.id === cycle.current_stage) : -1;
  const kpis = cycle?.kpis || {};
  const perFW = cycle?.per_framework || {};
  const fwList = cycle?.frameworks || [];

  const overallReadiness = fwList.length > 0
    ? Math.round(fwList.reduce((s, fw) => s + (perFW[fw]?.readiness || 0), 0) / fwList.length)
    : 0;

  // Flywheel spokes
  const flywheel = [
    {l:"Data Collected",icon:"📥",v:kpis.total_datapoints||0,mx:40,c:C.cyan,detail:`${kpis.approved_datapoints||0} approved`},
    {l:"Frameworks Mapped",icon:"🗺️",v:fwList.length,mx:12,c:C.blue,detail:`${fwList.length} active`},
    {l:"Ownership Chains",icon:"👤",v:kpis.approved_datapoints||0,mx:kpis.total_datapoints||1,c:C.amber,detail:`${Math.round((kpis.approved_datapoints||0)/(kpis.total_datapoints||1)*100)}% owned`},
    {l:"Audit Evidence",icon:"🛡️",v:kpis.verified_evidence||0,mx:kpis.total_evidence||1,c:C.emerald,detail:`${kpis.verified_evidence||0}/${kpis.total_evidence||0} verified`},
    {l:"Confidence Score",icon:"⭐",v:kpis.audit_readiness||0,mx:100,c:C.purple,detail:`${kpis.audit_readiness||0}/100`},
  ];

  return (
    <div style={{background:C.bg,minHeight:"100vh",color:C.text,fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      {/* Header */}
      <div style={{padding:"12px 20px 8px",borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,zIndex:10,background:C.bg+"f0",backdropFilter:"blur(12px)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:28,height:28,borderRadius:6,background:`linear-gradient(135deg,${C.teal},${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:C.white}}>D</div>
          <div><div style={{fontSize:14,fontWeight:800}}>DisclosureOS</div><div style={{fontSize:8,color:C.muted,letterSpacing:1}}>BOARD DASHBOARD</div></div>
          <div style={{flex:1}}/>
          {loading && <span style={{fontSize:8,color:C.amber}}>Refreshing…</span>}
          <Pl color={C.emerald} small glow>{aiHealth?.agents_available||0} AI agents</Pl>
          <Pl color={C.teal} small>Audit: {kpis.audit_readiness||0}%</Pl>
          <button onClick={fetchData} style={{padding:"4px 10px",borderRadius:5,border:`1px solid ${C.cyan}33`,background:"transparent",color:C.cyan,fontSize:9,fontWeight:600,cursor:"pointer"}}>↻ Refresh</button>
        </div>
      </div>

      <div style={{padding:"16px 20px 40px",maxWidth:1320,margin:"0 auto"}}>
        {/* ═══ ACTIVE CYCLE — 7-Stage Pipeline ═══ */}
        {cycle && <div style={{marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{fontSize:12,fontWeight:800,color:C.teal}}>🔄 ACTIVE CYCLE</span>
            <span style={{fontSize:10,color:C.text,fontWeight:600}}>{cycle.name}</span>
            <Pl color={C.amber} small>Deadline: {cycle.deadline}</Pl>
            <span style={{marginLeft:"auto",fontSize:9,color:C.muted}}>Stage {cycle.current_step}/7</span>
          </div>

          {/* Stage Pipeline */}
          <div style={{display:"flex",gap:3,alignItems:"center"}}>
            {STAGES.map((s,i) => {
              const isComplete = i < currentStageIdx;
              const isCurrent = i === currentStageIdx;
              const isFuture = i > currentStageIdx;
              return (
                <div key={s.id} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                  <div style={{width:"100%",height:4,borderRadius:2,background:isComplete?s.c:isCurrent?s.c+"66":C.border,transition:"background .3s"}}/>
                  <div style={{width:isCurrent?32:26,height:isCurrent?32:26,borderRadius:"50%",
                    background:isComplete?s.c+"22":isCurrent?s.c+"33":"transparent",
                    border:`2px solid ${isComplete?s.c:isCurrent?s.c:C.dim}`,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:isCurrent?14:11,transition:"all .3s",
                    boxShadow:isCurrent?`0 0 12px ${s.c}44`:"none"}}>
                    {isComplete?"✓":s.icon}
                  </div>
                  <span style={{fontSize:isCurrent?9:7,fontWeight:isCurrent?700:400,
                    color:isComplete?s.c:isCurrent?C.text:C.dim}}>{s.l}</span>
                </div>
              );
            })}
          </div>
        </div>}

        {/* ═══ TOP ROW: KPIs + Framework Readiness ═══ */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:12,marginBottom:14}}>
          {/* KPI Cards */}
          <div>
            <div style={{fontSize:9,fontWeight:700,color:C.cyan,marginBottom:6}}>CROSS-MODULE KPIs</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5}}>
              {[
                {l:"Datapoints",v:kpis.total_datapoints||0,sub:`${kpis.approved_datapoints||0} approved`,c:C.cyan},
                {l:"Evidence Docs",v:kpis.total_evidence||0,sub:`${kpis.verified_evidence||0} verified`,c:C.teal},
                {l:"Claims",v:kpis.total_claims||0,sub:`${kpis.approved_claims||0} approved`,c:C.purple},
                {l:"Quality Avg",v:`${kpis.quality_avg||0}%`,sub:"across all DPs",c:kpis.quality_avg>=80?C.emerald:C.amber},
                {l:"Audit Readiness",v:`${kpis.audit_readiness||0}%`,sub:"overall",c:kpis.audit_readiness>=70?C.emerald:kpis.audit_readiness>=40?C.amber:C.red},
                {l:"Overall Progress",v:`${overallReadiness}%`,sub:`${fwList.length} frameworks`,c:overallReadiness>=70?C.emerald:C.amber},
              ].map((k,i)=><div key={i} style={{padding:"10px 12px",borderRadius:7,background:C.card,border:`1px solid ${k.c}12`}}>
                <div style={{fontSize:18,fontWeight:800,color:k.c,lineHeight:1}}>{k.v}</div>
                <div style={{fontSize:8,fontWeight:600,marginTop:2}}>{k.l}</div>
                <div style={{fontSize:7,color:C.dim}}>{k.sub}</div>
              </div>)}
            </div>
          </div>

          {/* Framework Readiness */}
          <div>
            <div style={{fontSize:9,fontWeight:700,color:C.blue,marginBottom:6}}>FRAMEWORK READINESS</div>
            <div style={{padding:"12px 14px",borderRadius:8,background:C.card,border:`1px solid ${C.border}`}}>
              {fwList.map(fw => {
                const data = perFW[fw] || {};
                const pct = data.readiness || 0;
                const live = readiness[fw];
                const displayPct = live?.completion_pct ?? pct;
                return <div key={fw} style={{marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                    <span style={{fontSize:10,fontWeight:700,color:C.text,minWidth:100}}>{fw}</span>
                    <div style={{flex:1}}><Bar v={displayPct} mx={100} cl={displayPct>=80?C.emerald:displayPct>=50?C.amber:C.red} h={5}/></div>
                    <span style={{fontSize:10,fontWeight:800,color:displayPct>=80?C.emerald:displayPct>=50?C.amber:C.red,minWidth:35,textAlign:"right"}}>{displayPct}%</span>
                  </div>
                  <div style={{display:"flex",gap:8,fontSize:7,color:C.dim,paddingLeft:106}}>
                    <span>DPs: {data.dps_approved||0}/{data.dps_total||0}</span>
                    <span>Claims: {data.claims_approved||0}/{data.claims_generated||0}</span>
                    {live?.export_ready && <Pl color={C.emerald} small>Export Ready</Pl>}
                  </div>
                </div>;
              })}
            </div>
          </div>
        </div>

        {/* ═══ FLYWHEEL HEALTH ═══ */}
        <div style={{padding:"12px 16px",borderRadius:8,background:`linear-gradient(135deg,${C.purple}04,${C.emerald}04)`,border:`1px solid ${C.purple}18`,marginBottom:14}}>
          <div style={{fontSize:9,fontWeight:700,color:C.purple,marginBottom:8}}>DEFENSIBILITY FLYWHEEL</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
            {flywheel.map((f,i)=><div key={i} style={{textAlign:"center"}}>
              <div style={{fontSize:16,marginBottom:3}}>{f.icon}</div>
              <Bar v={f.v} mx={f.mx} cl={f.c} h={4}/>
              <div style={{fontSize:8,fontWeight:600,color:f.c,marginTop:2}}>{f.l}</div>
              <div style={{fontSize:7,color:C.dim}}>{f.detail}</div>
              {i<4&&<div style={{position:"absolute",right:-8,top:"50%",color:C.dim,fontSize:10}}>→</div>}
            </div>)}
          </div>
          <div style={{textAlign:"center",fontSize:7,color:C.dim,marginTop:6}}>Each cycle compounds: more data → richer maps → stronger ownership → deeper audit → higher confidence → LOOP</div>
        </div>

        {/* ═══ BOTTOM ROW: AI Status + Alerts ═══ */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {/* AI Intelligence */}
          <div style={{padding:"12px 14px",borderRadius:8,background:C.card,border:`1px solid ${C.purple}18`}}>
            <div style={{fontSize:9,fontWeight:700,color:C.purple,marginBottom:8}}>🧠 AI INTELLIGENCE</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:4}}>
              {(aiAgents||[]).slice(0,6).map(a=><div key={a.id||a.code} style={{padding:"6px 8px",borderRadius:5,background:C.surface,border:`1px solid ${a.status!=="unavailable"?C.emerald:C.red}15`}}>
                <div style={{fontSize:8,fontWeight:600}}>{a.name||a.id}</div>
                <Pl color={a.status!=="unavailable"?C.emerald:C.red} small>{a.status!=="unavailable"?"Online":"Offline"}</Pl>
              </div>)}
            </div>
            <div style={{display:"flex",gap:4,marginTop:6}}>
              <Pl color={C.emerald} small glow>5-Layer Guardrails Active</Pl>
              <Pl color={C.red} small>Human Gate Enforced</Pl>
            </div>
          </div>

          {/* Critical Alerts */}
          <div style={{padding:"12px 14px",borderRadius:8,background:C.card,border:`1px solid ${C.red}18`}}>
            <div style={{fontSize:9,fontWeight:700,color:C.red,marginBottom:8}}>🚨 ATTENTION REQUIRED</div>
            {[
              {l:"CSDDD: 3 critical suppliers require enhanced DD",c:C.red,route:"/csddd"},
              {l:"Scope 3 data gap: 30% spend missing (dp3)",c:C.amber,route:"/voluntary"},
              {l:"Art. 14 DD Statement: not yet generated",c:C.amber,route:"/csddd"},
              {l:"CDP deadline: Jul 31, 2026 (137 days)",c:C.blue,route:"/voluntary"},
              {l:`Audit readiness: ${kpis.audit_readiness||0}% — target 80%`,c:kpis.audit_readiness>=70?C.emerald:C.amber,route:"/audit"},
            ].map((a,i)=><div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 8px",borderRadius:4,background:C.surface,marginBottom:3,fontSize:8,cursor:"pointer"}}>
              <div style={{width:4,height:20,borderRadius:2,background:a.c}}/>
              <span style={{flex:1,color:C.text}}>{a.l}</span>
              <span style={{color:a.c,fontSize:7}}>→</span>
            </div>)}
          </div>
        </div>

        {/* Tagline */}
        <div style={{textAlign:"center",marginTop:18}}>
          <div style={{fontSize:12,fontWeight:300,color:C.muted,fontStyle:"italic"}}>"One datapoint. Every framework. <span style={{color:C.emerald,fontWeight:600}}>Zero duplicate effort.</span>"</div>
        </div>
      </div>
    </div>
  );
}
